import { useMutation, useQuery } from '@apollo/client';
import { ContactPageOutlined } from '@mui/icons-material';
import { FormSchemaProvider, FormSchemaProviderProps } from '@procureai/ui-form-mui';
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  WidgetEditActions,
  useUuid,
  useScrollIntoView,
  useCustomSnackbar,
} from '@procureai/ui-mui';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNegotiationDetail } from '../Module.hooks';
import { TsrctLanguage, TsrctNegotiationCaseStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { NegotiationContact } from '~/components/NegotiationContact/NegotiationContact';
import { SupplierContactHitId } from '~/components/SupplierContactAutocomplete';
import { useHashActionEdit } from '~/hooks/useHashActionEdit/useHashActionEdit';
import { ModuleHash } from '~/modules/Module.types';
import { SupplierContactForm } from '~/modules/NegotiationSupplierContactWidget/components/SupplierContactForm/SupplierContactForm';
import {
  supplierContactFormDataDefault,
  SupplierContactFormData,
  supplierContactFormDataSchema,
  TsrctSupplierContactMapper,
} from '~/modules/NegotiationSupplierContactWidget/components/SupplierContactForm/SupplierContactForm.schema';

import { NEGOTIATION_CONTACT_UPDATE_MUTATION } from './NegotiationContactMutation.gql';
import { NEGOTIATION_CONTACT_QUERY } from './NegotiationContactQuery.gql';

export interface NegotiationContactWidgetProps {
  // widget is part of a bigger form -> do not add own form and saving capabilities
  hasParentForm?: boolean;
}

export const NegotiationContactWidget = ({ hasParentForm }: NegotiationContactWidgetProps) => {
  const { negotiationId } = useNegotiationDetail();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiationContactWidget' });
  const [widgetRef, active, hashAction] = useScrollIntoView({ hash: ModuleHash.CONTACT });
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const formId = useUuid();

  const variables = {
    id: negotiationId,
  };

  const { data, loading } = useQuery(NEGOTIATION_CONTACT_QUERY, {
    variables,
    skip: !negotiationId,
  });

  const [contactUpdate, { loading: saving }] = useMutation(NEGOTIATION_CONTACT_UPDATE_MUTATION, {
    refetchQueries: [{ query: NEGOTIATION_CONTACT_QUERY, variables }],
    onError,
  });
  const [editOpen, setEditOpen] = useState(hasParentForm);

  const contentData = useMemo(
    () => (hasParentForm ? undefined : data?.tesseract.negotiationCase),
    [data, hasParentForm],
  );
  const handleEditToggle = useCallback(() => {
    setEditOpen((prev) => !prev);
  }, []);

  const handleEditApply: FormSchemaProviderProps<SupplierContactFormData>['onSubmit'] = (submitData) => {
    setEditOpen(false);
    if (submitData) {
      (async () => {
        const { errors } = await contactUpdate({
          variables: {
            id: negotiationId,
            contact: {
              firstName: submitData.supplier.contact?.firstName,
              lastName: submitData.supplier.contact?.lastName,
              mobile: submitData.supplier.contact?.mobile,
              language: submitData.supplier.contact?.language?.toUpperCase() as TsrctLanguage,
              phone: submitData.supplier.contact?.phone,
              email: submitData.supplier.contact?.email,
              internalId:
                submitData.supplier.contact?.internalId !== SupplierContactHitId.NEW
                  ? submitData.supplier.contact?.internalId
                  : null,
            },
          },
        });
        if (!errors?.length) {
          enqueueSuccessSnackbar(t('saveSuccess'));
        }
      })();
    }
  };

  const editDisabled = data?.tesseract.negotiationCase.status !== TsrctNegotiationCaseStatus.INITIALIZED;

  useHashActionEdit({
    id: data?.tesseract.negotiationCase?.id,
    active,
    editDisabled,
    editOpen,
    setEditOpen,
    hashAction,
  });

  const formData = useMemo(() => {
    if (contentData && editOpen && !hasParentForm) {
      return { supplier: TsrctSupplierContactMapper.parse(contentData.supplier) };
    }

    return null;
  }, [contentData, editOpen, hasParentForm]);

  let content = null;

  if (editOpen && contentData) {
    content = <SupplierContactForm disableSupplierChange />;

    if (!hasParentForm) {
      content = (
        <FormSchemaProvider<SupplierContactFormData>
          id={formId}
          data={formData}
          schema={supplierContactFormDataSchema}
          defaultValues={supplierContactFormDataDefault}
          onSubmit={handleEditApply}
        >
          {content}
        </FormSchemaProvider>
      );
    }
  } else if (loading || contentData) {
    content = <NegotiationContact data={contentData?.supplier.contact} loading={loading} />;
  }

  return (
    <>
      <Widget ref={widgetRef} active={active} hover={false} data-intercom-target="NegotiationContactWidget">
        <WidgetHeader
          title={t('title')}
          icon={<WidgetIcon icon={<ContactPageOutlined fontSize="small" />} />}
          actionsRight={
            hasParentForm ? null : (
              <WidgetEditActions
                formId={formId}
                open={editOpen}
                saving={saving}
                disabled={editDisabled}
                onToggle={handleEditToggle}
              />
            )
          }
        />
        <WidgetContent data-testid="NegotiationContactWidget">
          <div>{content}</div>
        </WidgetContent>
      </Widget>
    </>
  );
};
